/* ==========================================================================
   SURTAILLE — vanilla JS runtime
   Replaces the React interactivity: mobile menu, FAQ accordion, gallery
   carousel, bundle + size variant picker, and the AJAX cart drawer.
   No dependencies, no build step.
   ========================================================================== */
(function () {
  "use strict";

  var routes =
    (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";

  /* ----------------------------------------------------------------------
     Helpers
     ---------------------------------------------------------------------- */
  function qs(sel, root) {
    return (root || document).querySelector(sel);
  }

  function qsa(sel, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  }

  function on(el, type, handler, opts) {
    if (el) el.addEventListener(type, handler, opts);
  }

  function lockScroll(locked) {
    document.body.classList.toggle("is-locked", !!locked);
  }

  var toastTimer = null;
  function toast(message) {
    var el = qs("#theme-toast");
    if (!el) {
      el = document.createElement("div");
      el.id = "theme-toast";
      el.className = "toast";
      el.setAttribute("role", "status");
      el.setAttribute("aria-live", "polite");
      document.body.appendChild(el);
    }
    el.textContent = message;
    el.classList.add("is-visible");
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () {
      el.classList.remove("is-visible");
    }, 3200);
  }

  function parseJSON(el, fallback) {
    if (!el) return fallback;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return fallback;
    }
  }

  /* ----------------------------------------------------------------------
     Header — scroll shadow
     ---------------------------------------------------------------------- */
  function initHeader() {
    var header = qs("[data-header]");
    if (!header) return;

    // The old handler called classList.toggle on every scroll event, which
    // invalidates style on the sticky header dozens of times a second even
    // when nothing changes. Now the read is coalesced into one animation frame
    // and the class is only written when the state actually flips.
    var scrolled = null;
    var queued = false;

    var apply = function () {
      queued = false;
      var next = window.scrollY > 8;
      if (next === scrolled) return;
      scrolled = next;
      header.classList.toggle("is-scrolled", next);
    };

    var onScroll = function () {
      if (queued) return;
      queued = true;
      requestAnimationFrame(apply);
    };

    apply();
    window.addEventListener("scroll", onScroll, { passive: true });
  }

  /* ----------------------------------------------------------------------
     Mobile menu — full screen overlay
     ---------------------------------------------------------------------- */
  function initMobileMenu() {
    var menu = qs("[data-mobile-menu]");
    if (!menu) return;

    var openBtn = qs("[data-menu-open]");
    var closeBtns = qsa("[data-menu-close]");

    var setOpen = function (open) {
      menu.classList.toggle("is-open", open);
      menu.setAttribute("aria-hidden", open ? "false" : "true");
      if (openBtn) openBtn.setAttribute("aria-expanded", open ? "true" : "false");
      lockScroll(open);
      if (open) {
        var first = qs("a, button", menu);
        if (first) first.focus();
      } else if (openBtn) {
        openBtn.focus();
      }
    };

    on(openBtn, "click", function () {
      setOpen(true);
    });

    closeBtns.forEach(function (btn) {
      on(btn, "click", function () {
        setOpen(false);
      });
    });

    // Any in-menu navigation closes it (anchor links on a one-page layout).
    qsa("a", menu).forEach(function (link) {
      on(link, "click", function () {
        setOpen(false);
      });
    });

    on(document, "keydown", function (e) {
      if (e.key === "Escape" && menu.classList.contains("is-open")) setOpen(false);
    });
  }

  /* ----------------------------------------------------------------------
     FAQ accordion — one panel open at a time, like the React version
     ---------------------------------------------------------------------- */
  function initAccordions(root) {
    qsa("[data-accordion]", root).forEach(function (list) {
      var single = list.getAttribute("data-accordion") !== "multiple";

      qsa("[data-accordion-trigger]", list).forEach(function (trigger) {
        on(trigger, "click", function () {
          var item = trigger.closest("[data-accordion-item]");
          if (!item) return;
          var isOpen = item.classList.contains("is-open");

          if (single) {
            qsa("[data-accordion-item]", list).forEach(function (other) {
              if (other !== item) {
                other.classList.remove("is-open");
                var t = qs("[data-accordion-trigger]", other);
                if (t) t.setAttribute("aria-expanded", "false");
              }
            });
          }

          item.classList.toggle("is-open", !isOpen);
          trigger.setAttribute("aria-expanded", isOpen ? "false" : "true");
        });
      });
    });
  }

  /* ----------------------------------------------------------------------
     Product gallery — thumbnails + prev/next, no library
     ---------------------------------------------------------------------- */
  function initGallery(root) {
    qsa("[data-gallery]", root).forEach(function (gallery) {
      var slides = qsa("[data-gallery-slide]", gallery);
      var thumbs = qsa("[data-gallery-thumb]", gallery);
      if (slides.length === 0) return;

      var index = 0;

      var show = function (next) {
        index = (next + slides.length) % slides.length;
        slides.forEach(function (slide, i) {
          slide.classList.toggle("is-active", i === index);
          slide.setAttribute("aria-hidden", i === index ? "false" : "true");
        });
        thumbs.forEach(function (thumb, i) {
          thumb.classList.toggle("is-active", i === index);
          thumb.setAttribute("aria-current", i === index ? "true" : "false");
        });
      };

      thumbs.forEach(function (thumb, i) {
        on(thumb, "click", function () {
          show(i);
        });
      });

      on(qs("[data-gallery-prev]", gallery), "click", function () {
        show(index - 1);
      });
      on(qs("[data-gallery-next]", gallery), "click", function () {
        show(index + 1);
      });

      // Swipe on touch devices
      var startX = null;
      on(
        gallery,
        "touchstart",
        function (e) {
          startX = e.touches[0].clientX;
        },
        { passive: true }
      );
      on(
        gallery,
        "touchend",
        function (e) {
          if (startX === null) return;
          var delta = e.changedTouches[0].clientX - startX;
          if (Math.abs(delta) > 45) show(delta < 0 ? index + 1 : index - 1);
          startX = null;
        },
        { passive: true }
      );

      // Variant changes jump the gallery to the matching media
      gallery.__showMedia = function (mediaId) {
        if (!mediaId) return;
        for (var i = 0; i < slides.length; i++) {
          if (slides[i].getAttribute("data-media-id") === String(mediaId)) {
            show(i);
            return;
          }
        }
      };

      show(0);
    });
  }

  /* ----------------------------------------------------------------------
     Variant picker — ports the Lovable bundle + size logic
     Bundle option -> radio cards with badge, price per pair and savings.
     Every other option -> pills.
     ---------------------------------------------------------------------- */
  function initVariantPickers(root) {
    qsa("[data-product-form]", root).forEach(function (wrapper) {
      var data = parseJSON(qs("[data-variants-json]", wrapper), null);
      if (!data || !data.variants || data.variants.length === 0) return;

      var variants = data.variants;
      var idInput = qs("[data-variant-id]", wrapper);
      var submit = qs("[data-add-to-cart]", wrapper);
      var submitLabel = qs("[data-add-to-cart-label]", wrapper);
      var priceNow = qs("[data-price-now]", wrapper);
      var priceWas = qs("[data-price-was]", wrapper);
      var priceOff = qs("[data-price-off]", wrapper);
      var priceUnit = qs("[data-price-sub]", wrapper);
      var stockEl = qs("[data-stock]", wrapper);
      var gallery = qs("[data-gallery]", wrapper) || qs("[data-gallery]");

      // When pack cards are present they own the price block: a pack total is
      // a quantity total, not the unit price of the selected variant.
      var packMode = qsa("[data-pack]", wrapper).length > 0;

      // Current selection, indexed by option position (not DOM order).
      var groups = qsa("[data-option-values]", wrapper);
      var selected = [];
      groups.forEach(function (group) {
        var position = parseInt(group.getAttribute("data-option-position"), 10);
        var el = qs("[data-option-value].is-active", group) || qs("[data-option-value]", group);
        selected[position] = el ? el.getAttribute("data-value") : null;
      });
      for (var s = 0; s < selected.length; s++) {
        if (selected[s] === undefined) selected[s] = null;
      }

      // Only rewrite the URL once the shopper has actually picked something,
      // so landing pages do not gain a ?variant= on first paint.
      var touched = false;

      function findVariant(values) {
        for (var i = 0; i < variants.length; i++) {
          var v = variants[i];
          var match = true;
          for (var j = 0; j < values.length; j++) {
            if (values[j] !== null && v.options[j] !== values[j]) {
              match = false;
              break;
            }
          }
          if (match) return v;
        }
        return null;
      }

      // First variant carrying a given value at a given position — this is
      // what the source getBundlePrice() does for the bundle cards.
      function findVariantByValue(position, value) {
        var probe = selected.slice();
        probe[position] = value;
        return findVariant(probe) || findVariant(
          variants[0].options.map(function (_, j) {
            return j === position ? value : null;
          })
        );
      }

      function valueIsAvailable(position, value) {
        for (var i = 0; i < variants.length; i++) {
          var v = variants[i];
          if (v.options[position] !== value) continue;
          var ok = true;
          for (var j = 0; j < selected.length; j++) {
            if (j !== position && selected[j] !== null && v.options[j] !== selected[j]) {
              ok = false;
              break;
            }
          }
          if (ok && v.available) return true;
        }
        return false;
      }

      function setText(el, value) {
        if (!el) return;
        if (value === null || value === undefined || value === "") {
          el.hidden = true;
          el.textContent = "";
        } else {
          el.hidden = false;
          el.textContent = value;
        }
      }

      function render() {
        var variant = findVariant(selected);

        // Option chips: active + availability state
        qsa("[data-option-values]", wrapper).forEach(function (group) {
          var position = parseInt(group.getAttribute("data-option-position"), 10);
          qsa("[data-option-value]", group).forEach(function (el) {
            var value = el.getAttribute("data-value");
            var isActive = selected[position] === value;
            el.classList.toggle("is-active", isActive);
            el.setAttribute("aria-pressed", isActive ? "true" : "false");
            var available = valueIsAvailable(position, value);
            el.classList.toggle("is-soldout", !available);

            // Bundle cards recompute their own price/discount for the
            // current size, exactly like BundleCards in the source.
            if (el.hasAttribute("data-bundle-card")) {
              var bv = findVariantByValue(position, value);
              if (bv) {
                setText(qs("[data-bundle-total]", el), bv.price_formatted);
                setText(
                  qs("[data-bundle-compare]", el),
                  bv.compare_at_price > bv.price ? bv.compare_at_price_formatted : null
                );
                setText(qs("[data-bundle-unit]", el), bv.unit_label);
                setText(
                  qs("[data-bundle-pct]", el),
                  bv.discount_pct > 0 ? "— " + bv.discount_pct + " %" : null
                );
                var saveEl = qs("[data-bundle-save]", el);
                if (saveEl) {
                  if (bv.discount_pct > 0 && saveEl.getAttribute("data-save-template")) {
                    saveEl.hidden = false;
                    saveEl.textContent = saveEl
                      .getAttribute("data-save-template")
                      .replace("{{ amount }}", bv.savings_formatted);
                  } else {
                    saveEl.hidden = true;
                  }
                }
              }
            }
          });
        });

        // Price block — skipped entirely in pack mode
        if (packMode) {
          // nothing: initPackSelectors drives the price block
        } else if (variant) {
          setText(priceNow, variant.price_formatted);
          setText(
            priceWas,
            variant.compare_at_price > variant.price ? variant.compare_at_price_formatted : null
          );
          setText(priceOff, variant.discount_pct > 0 ? "-" + variant.discount_pct + " %" : null);
          if (priceUnit) {
            var tpl = priceUnit.getAttribute("data-unit-template") || "{{ amount }}";
            if (variant.unit_label) {
              priceUnit.hidden = false;
              priceUnit.textContent = tpl.replace("{{ amount }}", variant.unit_label);
            } else {
              priceUnit.hidden = true;
            }
          }
        } else {
          setText(priceNow, "—");
          setText(priceWas, null);
          setText(priceOff, null);
          if (priceUnit) priceUnit.hidden = true;
        }

        // Hidden input + submit state
        if (idInput) idInput.value = variant ? variant.id : "";

        if (submit) {
          var unavailable = !variant;
          var soldOut = variant && !variant.available;
          submit.disabled = unavailable || soldOut;
          if (submitLabel) {
            var key = unavailable
              ? "unavailable"
              : soldOut
                ? "sold_out"
                : "add";
            submitLabel.textContent =
              submit.getAttribute("data-label-" + key) || submitLabel.textContent;
          }
        }

        if (stockEl) {
          var inStock = !!(variant && variant.available);
          stockEl.hidden = false;
          stockEl.classList.toggle("buy__stock--out", !inStock);
          var stockText = stockEl.getAttribute(inStock ? "data-in-stock" : "data-out-stock");
          var label = qs("[data-stock-label]", stockEl);
          if (label && stockText) label.textContent = stockText;
        }

        // Gallery follows the variant image
        if (gallery && gallery.__showMedia && variant && variant.media_id) {
          gallery.__showMedia(variant.media_id);
        }

        // Update the URL so the variant is shareable / restorable.
        //
        // The hash has to go. Shoppers reach this block through the #acheter
        // anchor, so location.href still carries that fragment; replacing the
        // URL while it is present makes mobile browsers re-resolve the anchor
        // and scroll to it — the page appeared to jump down on every option
        // change. Dropping the fragment keeps the scroll position untouched
        // and the ?variant= link just as shareable.
        if (touched && variant && data.update_url && history.replaceState) {
          var url = new URL(window.location.href);
          url.hash = "";
          url.searchParams.set("variant", variant.id);
          history.replaceState({}, "", url.toString());
        }

        wrapper.dispatchEvent(
          new CustomEvent("variant:change", { detail: { variant: variant }, bubbles: true })
        );
      }

      // Restore ?variant= from the URL on load
      var urlVariant = new URLSearchParams(window.location.search).get("variant");
      if (urlVariant) {
        for (var i = 0; i < variants.length; i++) {
          if (String(variants[i].id) === urlVariant) {
            selected = variants[i].options.slice(0, selected.length);
            break;
          }
        }
      }

      qsa("[data-option-value]", wrapper).forEach(function (el) {
        on(el, "click", function () {
          var group = el.closest("[data-option-values]");
          var position = parseInt(group.getAttribute("data-option-position"), 10);
          selected[position] = el.getAttribute("data-value");
          touched = true;
          render();
        });
      });

      render();
    });
  }

  /* ----------------------------------------------------------------------
     Pack selector — quantity only, never a variant
     Each card carries its already-formatted prices in data attributes, so no
     money formatting happens here. Selecting a pack rewrites the hidden
     quantity input and the price block; the variant id is untouched.
     ---------------------------------------------------------------------- */
  function initPackSelectors(root) {
    qsa("[data-product-form]", root).forEach(function (wrapper) {
      var packs = qsa("[data-pack]", wrapper);
      if (packs.length === 0) return;

      var qtyInput = qs("[data-quantity-input]", wrapper);
      var priceNow = qs("[data-price-now]", wrapper);
      var pricePer = qs("[data-price-per]", wrapper);
      var priceWas = qs("[data-price-was]", wrapper);
      var priceOff = qs("[data-price-off]", wrapper);
      var priceSub = qs("[data-price-sub]", wrapper);

      function apply(el) {
        packs.forEach(function (p) {
          var on = p === el;
          p.classList.toggle("is-active", on);
          p.setAttribute("aria-checked", on ? "true" : "false");
          p.tabIndex = on ? 0 : -1;
        });

        if (qtyInput) qtyInput.value = el.getAttribute("data-quantity") || "1";

        // The headline figure is the price per pair; the lot total goes to the
        // secondary line so nobody reads a lot total as a unit price.
        var unit = el.getAttribute("data-unit");
        var standard = el.getAttribute("data-standard");
        var pct = parseInt(el.getAttribute("data-pct"), 10) || 0;
        var sub = el.getAttribute("data-sub");

        if (priceNow && unit) priceNow.textContent = unit;
        if (pricePer) pricePer.hidden = false;

        if (priceWas) {
          if (standard) {
            priceWas.hidden = false;
            priceWas.textContent = standard;
          } else {
            priceWas.hidden = true;
          }
        }

        if (priceOff) {
          if (pct > 0) {
            priceOff.hidden = false;
            priceOff.textContent = "-" + pct + " %";
          } else {
            priceOff.hidden = true;
          }
        }

        if (priceSub) {
          if (sub) {
            priceSub.hidden = false;
            priceSub.textContent = sub;
          } else {
            priceSub.hidden = true;
          }
        }
      }

      packs.forEach(function (el, i) {
        on(el, "click", function () {
          apply(el);
        });

        // Radio group keyboard behaviour
        on(el, "keydown", function (e) {
          var next = null;
          if (e.key === "ArrowRight" || e.key === "ArrowDown") next = packs[(i + 1) % packs.length];
          if (e.key === "ArrowLeft" || e.key === "ArrowUp")
            next = packs[(i - 1 + packs.length) % packs.length];
          if (!next) return;
          e.preventDefault();
          apply(next);
          next.focus();
        });
      });

      apply(qs("[data-pack].is-active", wrapper) || packs[0]);
    });
  }

  /* ----------------------------------------------------------------------
     Cart — AJAX add / change / remove, drawer, section re-render
     ---------------------------------------------------------------------- */
  var Cart = {
    busy: false,

    drawer: function () {
      return qs("[data-cart-drawer]");
    },

    setOpen: function (open) {
      var drawer = Cart.drawer();
      if (!drawer) return;
      drawer.classList.toggle("is-open", open);
      drawer.setAttribute("aria-hidden", open ? "false" : "true");
      lockScroll(open);
      if (open) {
        var close = qs("[data-cart-close]", drawer);
        if (close) close.focus();
      }
    },

    setBusy: function (busy) {
      Cart.busy = busy;
      var drawer = Cart.drawer();
      if (drawer) drawer.classList.toggle("is-busy", busy);
    },

    updateCount: function (count) {
      qsa("[data-cart-count]").forEach(function (el) {
        el.textContent = count;
        el.hidden = count === 0;
      });
    },

    // Re-render the drawer (and the cart page when we are on it) from the
    // Section Rendering API, so all money formatting stays in Liquid.
    refresh: function () {
      var names = ["cart-drawer"];
      if (qs("[data-cart-page]")) names.push("main-cart");

      return fetch(routes.replace(/\/$/, "") + "/?sections=" + names.join(","), {
        headers: { "X-Requested-With": "XMLHttpRequest" },
      })
        .then(function (res) {
          return res.json();
        })
        .then(function (sections) {
          names.forEach(function (name) {
            var html = sections[name];
            if (!html) return;
            var host = qs("#shopify-section-" + name);
            if (!host) return;
            var wasOpen = !!host.querySelector(".drawer.is-open");

            // The Section Rendering API returns the section wrapper too;
            // unwrap it so we do not nest #shopify-section-* divs.
            var parsed = new DOMParser().parseFromString(html, "text/html");
            var incoming = parsed.querySelector("#shopify-section-" + name);
            host.innerHTML = incoming ? incoming.innerHTML : html;

            if (wasOpen) {
              var d = qs("[data-cart-drawer]", host);
              if (d) {
                d.classList.add("is-open");
                d.setAttribute("aria-hidden", "false");
              }
            }
            Cart.bind(host);
          });
          return fetch(routes.replace(/\/$/, "") + "/cart.js")
            .then(function (r) {
              return r.json();
            })
            .then(function (cart) {
              Cart.updateCount(cart.item_count);
              return cart;
            });
        });
    },

    add: function (form) {
      if (Cart.busy) return;
      var submit = qs("[data-add-to-cart]", form);
      var label = qs("[data-add-to-cart-label]", form);
      var previous = label ? label.textContent : null;

      Cart.setBusy(true);
      if (submit) submit.disabled = true;
      if (label && submit && submit.getAttribute("data-label-adding")) {
        label.textContent = submit.getAttribute("data-label-adding");
      }

      var body = new FormData(form);

      return fetch(routes.replace(/\/$/, "") + "/cart/add.js", {
        method: "POST",
        headers: { Accept: "application/json", "X-Requested-With": "XMLHttpRequest" },
        body: body,
      })
        .then(function (res) {
          return res.json().then(function (json) {
            if (!res.ok) throw json;
            return json;
          });
        })
        .then(function () {
          return Cart.refresh();
        })
        .then(function () {
          Cart.setOpen(true);
        })
        .catch(function (err) {
          toast((err && (err.description || err.message)) || "Erreur");
        })
        .then(function () {
          Cart.setBusy(false);
          if (submit) submit.disabled = false;
          if (label && previous !== null) label.textContent = previous;
        });
    },

    change: function (line, quantity) {
      if (Cart.busy) return;
      Cart.setBusy(true);

      return fetch(routes.replace(/\/$/, "") + "/cart/change.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: JSON.stringify({ line: line, quantity: quantity }),
      })
        .then(function (res) {
          return res.json().then(function (json) {
            if (!res.ok) throw json;
            return json;
          });
        })
        .then(function () {
          return Cart.refresh();
        })
        .catch(function (err) {
          toast((err && (err.description || err.message)) || "Erreur");
        })
        .then(function () {
          Cart.setBusy(false);
        });
    },

    // (Re)attach listeners — called on load and after each section swap.
    bind: function (root) {
      qsa("[data-cart-open]", root).forEach(function (btn) {
        if (btn.__bound) return;
        btn.__bound = true;
        on(btn, "click", function (e) {
          e.preventDefault();
          Cart.setOpen(true);
        });
      });

      qsa("[data-cart-close]", root).forEach(function (btn) {
        on(btn, "click", function () {
          Cart.setOpen(false);
        });
      });

      qsa("[data-cart-line-change]", root).forEach(function (btn) {
        on(btn, "click", function () {
          var line = parseInt(btn.getAttribute("data-line"), 10);
          var qty = parseInt(btn.getAttribute("data-quantity"), 10);
          Cart.change(line, Math.max(qty, 0));
        });
      });

      qsa("[data-cart-line-qty]", root).forEach(function (input) {
        on(input, "change", function () {
          var line = parseInt(input.getAttribute("data-line"), 10);
          var qty = parseInt(input.value, 10);
          Cart.change(line, isNaN(qty) || qty < 0 ? 0 : qty);
        });
      });
    },
  };

  function initCart() {
    Cart.bind(document);

    on(document, "keydown", function (e) {
      var drawer = Cart.drawer();
      if (e.key === "Escape" && drawer && drawer.classList.contains("is-open")) {
        Cart.setOpen(false);
      }
    });

    // Intercept every product form so add-to-cart stays on the page.
    on(document, "submit", function (e) {
      var form = e.target;
      if (!form.matches || !form.matches("[data-cart-add-form]")) return;
      e.preventDefault();
      Cart.add(form);
    });
  }

  /* ----------------------------------------------------------------------
     Sticky mobile CTA — reserve space so it never covers the footer
     ---------------------------------------------------------------------- */
  function initStickyCta() {
    if (qs("[data-sticky-cta]")) document.body.classList.add("has-sticky-cta");
  }

  /* ----------------------------------------------------------------------
     Reveal-on-scroll for the fade-up animation
     ---------------------------------------------------------------------- */
  function initReveal() {
    var targets = qsa("[data-reveal]");
    if (targets.length === 0 || !("IntersectionObserver" in window)) {
      targets.forEach(function (el) {
        el.classList.add("fade-up");
      });
      return;
    }

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add("fade-up");
            io.unobserve(entry.target);
          }
        });
      },
      { rootMargin: "0px 0px -10% 0px" }
    );

    targets.forEach(function (el) {
      io.observe(el);
    });
  }

  /* ----------------------------------------------------------------------
     Boot
     ---------------------------------------------------------------------- */
  function boot(root) {
    initAccordions(root);
    initGallery(root);
    initVariantPickers(root);
    initPackSelectors(root);
  }

  function init() {
    initHeader();
    initMobileMenu();
    initCart();
    initStickyCta();
    initReveal();
    boot(document);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  /* Theme editor: re-initialise a section after it is re-rendered. */
  document.addEventListener("shopify:section:load", function (e) {
    boot(e.target);
    Cart.bind(e.target);
    initHeader();
    initMobileMenu();
    initStickyCta();
    initReveal();
  });

  window.ThemeCart = Cart;
})();
